import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const getDefaultLang = () => {
  const lang = navigator.language || navigator.userLanguage || '';
  return lang.startsWith('zh') ? 'zh' : 'en';
};

const StudentRewards = () => {
  const [lang, setLang] = useState(getDefaultLang);

  const content = useMemo(() => {
    if (lang === 'zh') {
      return {
        title: 'GATE Selective Entry Exam',
        subtitle: 'Uplus Education 学员荣获佳绩',
        items: [
          { highlight: '4名', text: '学员在2025年GATE考试中总分突破300分（302 / 307 / 308 / 309 —— 来自同一个5人封闭高阶班级）。' },
          { highlight: '1名', text: '学员在2025年GATE考试中荣获Quantitative Reasoning State Highest 数学逻辑州最高分。' },
          { highlight: '4名', text: '学员在2025年GATE考试中荣获Communicating Ideas State Highest 写作州最高分。' },
          { highlight: '每年30+', text: '学生都获得了Perth Modern Offer / 私校奖学金。' },
        ],
        body: [
          <>成绩的背后，是长期、可持续的教学体系。在过往教学中，Uplus Education 累计已有<Red>260+名</Red>学员成功考入GATE班级或获得私校奖学金。</>,
          '我们始终相信，成绩只是结果，而非唯一目标。Uplus的教学成果，来自系统化的课程设计、长期坚持的教学执行、家长的高度配合，以及学生日复一日的努力与良好学习习惯的养成。',
          '每一份亮眼成绩，都是多方共同投入、长期积累后的自然结果。',
        ],
        toggle: 'English',
      };
    }
    return {
      title: 'GATE Selective Entry Exam',
      subtitle: 'Outstanding Results from Uplus Education Students',
      items: [
        { highlight: '4 students', text: 'scored above 300 in the 2025 GATE exam (302 / 307 / 308 / 309 — all from the same 5-student advanced class).' },
        { highlight: '1 student', text: 'achieved the State Highest score in 2025 GATE Quantitative Reasoning.' },
        { highlight: '4 students', text: 'have achieved the State Highest score in 2025 GATE Communicating Ideas.' },
        { highlight: '30+ students', text: 'receive Perth Modern Offers or private school scholarships every year.' },
      ],
      body: [
        <>Behind every result is a long-term, sustainable teaching system. To date, Uplus Education has helped over <Red>260 students</Red> gain entry into GATE programs or receive private school scholarships.</>,
        'We have always believed that results are an outcome, not the sole objective. Our achievements stem from systematic curriculum design, consistent teaching execution, strong parental support, and the daily effort and good study habits cultivated by our students.',
        'Every outstanding result is the natural product of collective commitment and long-term dedication.',
      ],
      toggle: '中文',
    };
  }, [lang]);

  return (
    <Container>
      <Helmet>
        <title>Student Achievements - Uplus Education</title>
        <meta name="description" content="Outstanding results from Uplus Education students in 2025 GATE Selective Entry Exam" />
      </Helmet>

      <Card>
        <ToggleButton onClick={() => setLang(l => l === 'zh' ? 'en' : 'zh')}>
          {content.toggle}
        </ToggleButton>

        <TitleLine>{content.title}</TitleLine>
        <Subtitle>{content.subtitle}</Subtitle>

        <Divider />

        <ItemList>
          {content.items.map((item, i) => (
            <Item key={i}>
              <Bullet />
              <ItemText>
                <Red>{item.highlight}</Red>{' '}{item.text}
              </ItemText>
            </Item>
          ))}
        </ItemList>

        <Divider />

        <BodySection>
          {content.body.map((paragraph, i) => (
            <BodyParagraph key={i}>{paragraph}</BodyParagraph>
          ))}
        </BodySection>
      </Card>
    </Container>
  );
};

export default StudentRewards;

/* ---- styled-components ---- */

const Container = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  font-family: ${theme.fonts.main};
  background: ${theme.colors.background};
  min-height: calc(100vh - 56px);
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media (max-width: 600px) {
    padding: 1.5rem 1rem 3rem;
  }
`;

const Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 3rem 3.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;

  @media (max-width: 600px) {
    padding: 2rem 1.5rem;
    border-radius: 12px;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.primary};
    color: #fff;
    border-color: ${theme.colors.primary};
  }
`;

const TitleLine = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.4rem;
  line-height: 1.4;
  padding-right: 80px;

  @media (max-width: 600px) {
    font-size: 1.25rem;
    padding-right: 70px;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0;
  line-height: 1.5;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 1.5rem 0;
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Bullet = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  margin-top: 8px;
`;

const ItemText = styled.span`
  font-size: 1rem;
  line-height: 1.65;
  color: #374151;
`;

const Red = styled.span`
  color: #ef4444;
  font-weight: 600;
`;

const BodySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BodyParagraph = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: #4b5563;
  margin: 0;
`;
